/**
 * 底部抽屉 - 添加/编辑地址
 * 支持新增和编辑两种模式
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { saveAddress } from "@/api/address";
import type { AddressItem, AddressEditParams } from "@/api/address";

const PRIMARY = "#FF6B35";

interface Props {
  open: boolean;
  address: AddressItem | null;  // null=新增模式, 有值=编辑模式
  initialLocation?: { city: string; township: string; lng: string; lat: string } | null; // 地图选址返回的初始位置
  onClose: (saved?: boolean) => void;
}

/** 省市区数据（示例数据，实际应该从接口获取） */
const CITY_DATA = {
  "四川省": ["成都市", "绵阳市", "德阳市", "南充市", "宜宾市"],
  "陕西省": ["西安市", "宝鸡市", "咸阳市", "渭南市", "延安市"],
  "北京市": ["北京市"],
  "上海市": ["上海市"],
  "广东省": ["广州市", "深圳市", "东莞市", "佛山市", "珠海市"],
};

const DISTRICT_DATA: Record<string, string[]> = {
  "成都市": ["锦江区", "青羊区", "金牛区", "武侯区", "成华区", "双流区", "高新区"],
  "西安市": ["新城区", "碑林区", "莲湖区", "雁塔区", "未央区", "长安区"],
  "广州市": ["越秀区", "海珠区", "荔湾区", "天河区", "白云区", "番禺区"],
  "深圳市": ["罗湖区", "福田区", "南山区", "宝安区", "龙岗区", "龙华区"],
  "北京市": ["东城区", "西城区", "朝阳区", "丰台区", "海淀区", "通州区"],
  "上海市": ["黄浦区", "徐汇区", "长宁区", "静安区", "普陀区", "浦东新区"],
};

export default function AddressDrawer({ open, address, initialLocation, onClose }: Props) {
  const navigate = useNavigate();

  // 表单状态
  const [formData, setFormData] = useState({
    real_name: "",
    phone: "",
    province: "",
    city: "",
    district: "",
    detail: "",
    city_id: 0,
    longitude: "",
    latitude: "",
  });

  const [loading, setLoading] = useState(false);
  const [showProvincePicker, setShowProvincePicker] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showDistrictPicker, setShowDistrictPicker] = useState(false);
  const [currentPickerType, setCurrentPickerType] = useState<"province" | "city" | "district">("province");

  /** 初始化表单数据（编辑模式 / 新增模式 / 地图选址回填） */
  useEffect(() => {
    if (open) {
      if (address) {
        // 编辑模式
        setFormData({
          real_name: address.real_name || "",
          phone: address.phone || "",
          province: address.province || "",
          city: address.city || "",
          district: address.district || "",
          detail: address.detail || "",
          city_id: address.city_id || 0,
          longitude: (address as any).longitude || "",
          latitude: (address as any).latitude || "",
        });
      } else if (initialLocation?.city) {
        // 新增模式 + 从地图选址返回 → 回填省市区+经纬度
        const city = initialLocation.city;
        const province = city.replace(/(市|区).*$/, "") || city;
        const cityName = city.match(/([\u4e00-\u9fa5]+市)/)?.[1] || "";
        setFormData({
          real_name: "",
          phone: "",
          province,
          city: cityName,
          district: initialLocation.township || "",
          detail: "",
          city_id: 0,
          longitude: initialLocation.lng || "",
          latitude: initialLocation.lat || "",
        });
      } else {
        // 新增模式 - 纯净重置
        setFormData({
          real_name: "",
          phone: "",
          province: "",
          city: "",
          district: "",
          detail: "",
          city_id: 0,
          longitude: "",
          latitude: "",
        });
      }
      // 关闭所有选择器
      setShowProvincePicker(false);
      setShowCityPicker(false);
      setShowDistrictPicker(false);
    }
  }, [open, address, initialLocation]);

  /** 省份列表 */
  const provinceList = Object.keys(CITY_DATA);

  /** 城市列表（根据选中的省份） */
  const cityList = formData.province ? (CITY_DATA[formData.province] || []) : [];

  /** 区/街道列表（根据选中的城市） */
  const districtList = formData.city ? (DISTRICT_DATA[formData.city] || []) : [];

  /** 更新表单字段 */
  const updateField = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /** 选择省份 */
  const handleSelectProvince = (province: string) => {
    setFormData(prev => ({
      ...prev,
      province,
      city: "",       // 重置城市和区
      district: "",
    }));
    setShowProvincePicker(false);
    // 自动打开城市选择
    setTimeout(() => {
      setCurrentPickerType("city");
      setShowCityPicker(true);
    }, 300);
  };

  /** 选择城市 */
  const handleSelectCity = (city: string) => {
    setFormData(prev => ({
      ...prev,
      city,
      district: "",   // 重置区
    }));
    setShowCityPicker(false);
    // 自动打开区选择
    setTimeout(() => {
      setCurrentPickerType("district");
      setShowDistrictPicker(true);
    }, 300);
  };

  /** 选择区 */
  const handleSelectDistrict = (district: string) => {
    setFormData(prev => ({ ...prev, district }));
    setShowDistrictPicker(false);
  };

  /** 点击所在地区 → 跳转地图选址页面 */
  const handleJumpToLocationSelect = () => {
    navigate("/location-select", {
      state: { from: "/address-select" },
    });
  };

  /** 提交表单 */
  const handleSubmit = async () => {
    // 表单验证
    if (!formData.real_name.trim()) {
      alert("请输入收货人姓名");
      return;
    }
    if (!formData.phone.trim()) {
      alert("请输入手机号");
      return;
    }
    if (!/^1\d{10}$/.test(formData.phone)) {
      alert("请输入正确的手机号");
      return;
    }
    if (!formData.province) {
      alert("请选择所在地区");
      return;
    }
    if (!formData.detail.trim()) {
      alert("请输入详细地址");
      return;
    }

    setLoading(true);
    try {
      const params: AddressEditParams = {
        id: address?.id,
        real_name: formData.real_name.trim(),
        phone: formData.phone.trim(),
        detail: formData.detail.trim(),
        is_read_agreement: 1,
        city_id: formData.city_id,
        address: {
          province: formData.province,
          city: formData.city,
          district: formData.district,
          city_id: formData.city_id,
        },
        ...(formData.longitude && { longitude: formData.longitude }),
        ...(formData.latitude && { latitude: formData.latitude }),
      };

      await saveAddress(params);
      onClose(true);  // 通知父组件刷新列表
    } catch (err: any) {
      console.error("[地址抽屉] 保存失败:", err);
      alert(err.message || "保存失败，请重试");
    } finally {
      setLoading(false);
    }
  };

  /** 遮罩层点击关闭 */
  const handleMaskClick = () => {
    onClose(false);
  };

  if (!open) return null;

  return (
    <>
      {/* 遮罩层 */}
      <div
        onClick={handleMaskClick}
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 999, transition: "opacity 0.3s",
        }}
      />

      {/* 抽屉内容 */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "#fff",
        borderRadius: "16px 16px 0 0",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
        animation: "slideUp 0.3s ease-out",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}>
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}</style>

        {/* 头部 */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid #f0f0f0",
        }}>
          <button
            onClick={() => onClose(false)}
            style={{ fontSize: 14, color: "#666", background: "none", border: "none", cursor: "pointer" }}
          >
            取消
          </button>
          <span style={{ fontSize: 17, fontWeight: 600, color: "#333" }}>
            {address ? "编辑地址" : "新增地址"}
          </span>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              fontSize: 15, fontWeight: 600,
              color: loading ? "#ccc" : PRIMARY,
              background: "none", border: "none", cursor: loading ? "default" : "pointer",
            }}
          >
            {loading ? "保存中..." : "保存"}
          </button>
        </div>

        {/* 表单区域 */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px 24px" }}>
          {/* 收货人姓名 */}
          <div style={{
            display: "flex", alignItems: "center",
            padding: "14px 0", borderBottom: "1px solid #f5f5f5",
          }}>
            <label style={{ width: 80, fontSize: 14, color: "#333", flexShrink: 0 }}>
              收货人
            </label>
            <input
              type="text"
              placeholder="请输入收货人姓名"
              value={formData.real_name}
              onChange={(e) => updateField("real_name", e.target.value)}
              style={{
                flex: 1, fontSize: 14, color: "#333", border: "none", outline: "none",
                background: "transparent",
              }}
            />
          </div>

          {/* 手机号 */}
          <div style={{
            display: "flex", alignItems: "center",
            padding: "14px 0", borderBottom: "1px solid #f5f5f5",
          }}>
            <label style={{ width: 80, fontSize: 14, color: "#333", flexShrink: 0 }}>
              手机号
            </label>
            <input
              type="tel"
              maxLength={11}
              placeholder="请输入手机号"
              value={formData.phone}
              onChange={(e) => updateField("phone", e.target.value.replace(/\D/g, ""))}
              style={{
                flex: 1, fontSize: 14, color: "#333", border: "none", outline: "none",
                background: "transparent",
              }}
            />
          </div>

          {/* 所在地区 */}
          <div
            onClick={handleJumpToLocationSelect}
            style={{
              display: "flex", alignItems: "center",
              padding: "14px 0", borderBottom: "1px solid #f5f5f5", cursor: "pointer",
            }}
          >
            <label style={{ width: 80, fontSize: 14, color: "#333", flexShrink: 0 }}>
              所在地区
            </label>
            <div style={{ flex: 1, fontSize: 14, color: formData.province ? "#333" : "#999" }}>
              {formData.province
                ? `${formData.province} ${formData.city || ""} ${formData.district || ""}`.trim()
                : "请选择省/市/区"}
            </div>
            <i className="ri-arrow-right-s-line" style={{ fontSize: 18, color: "#ccc" }} />
          </div>

          {/* 详细地址 */}
          <div style={{
            display: "flex", alignItems: "center",
            padding: "14px 0", borderBottom: "1px solid #f5f5f5",
          }}>
            <label style={{ width: 80, fontSize: 14, color: "#333", flexShrink: 0 }}>
              详细地址
            </label>
            <input
              type="text"
              placeholder="请输入详细地址（如：XX路XX号）"
              value={formData.detail}
              onChange={(e) => updateField("detail", e.target.value)}
              style={{
                flex: 1, fontSize: 14, color: "#333", border: "none", outline: "none",
                background: "transparent",
              }}
            />
          </div>

          {/* 设为默认地址（仅新增时显示） */}
          {!address && (
            <div style={{
              display: "flex", alignItems: "center",
              padding: "14px 0",
            }}>
              <label style={{ width: 80, fontSize: 14, color: "#333", flexShrink: 0 }}>
                默认地址
              </label>
              <div style={{ flex: 1, fontSize: 13, color: "#999" }}>
                新增的第一个地址将自动设为默认
              </div>
            </div>
          )}
        </div>

        {/* ====== 省份选择器 ====== */}
        {showProvincePicker && (
          <>
            <div
              onClick={() => setShowProvincePicker(false)}
              style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.3)", zIndex: 1001,
              }}
            />
            <div style={{
              position: "fixed", bottom: 0, left: 0, right: 0,
              background: "#fff", borderRadius: "12px 12px 0 0",
              zIndex: 1002, maxHeight: "60vh",
            }}>
              <div style={{
                padding: "16px 20px", textAlign: "center",
                fontSize: 16, fontWeight: 600, color: "#333",
                borderBottom: "1px solid #f0f0f0",
              }}>选择省份</div>
              <div style={{ overflowY: "auto", maxHeight: "calc(60vh - 52px)", paddingBottom: 20 }}>
                {provinceList.map((p) => (
                  <div key={p} onClick={() => handleSelectProvince(p)}
                    style={{
                      padding: "14px 20px", fontSize: 14,
                      borderBottom: "1px solid #f9f9f9", cursor: "pointer",
                      background: p === formData.province ? "#fff5f0" : "transparent",
                      color: p === formData.province ? PRIMARY : "#333",
                      fontWeight: p === formData.province ? 600 : 400,
                    }}
                  >
                    {p}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ====== 城市选择器 ====== */}
        {showCityPicker && (
          <>
            <div
              onClick={() => setShowCityPicker(false)}
              style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.3)", zIndex: 1001,
              }}
            />
            <div style={{
              position: "fixed", bottom: 0, left: 0, right: 0,
              background: "#fff", borderRadius: "12px 12px 0 0",
              zIndex: 1002, maxHeight: "60vh",
            }}>
              <div style={{
                padding: "16px 20px", textAlign: "center",
                fontSize: 16, fontWeight: 600, color: "#333",
                borderBottom: "1px solid #f0f0f0",
              }}>选择城市</div>
              <div style={{ overflowY: "auto", maxHeight: "calc(60vh - 52px)", paddingBottom: 20 }}>
                {cityList.map((c) => (
                  <div key={c} onClick={() => handleSelectCity(c)}
                    style={{
                      padding: "14px 20px", fontSize: 14,
                      borderBottom: "1px solid #f9f9f9", cursor: "pointer",
                      background: c === formData.city ? "#fff5f0" : "transparent",
                      color: c === formData.city ? PRIMARY : "#333",
                      fontWeight: c === formData.city ? 600 : 400,
                    }}
                  >
                    {c}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ====== 区/街道选择器 ====== */}
        {showDistrictPicker && (
          <>
            <div
              onClick={() => setShowDistrictPicker(false)}
              style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.3)", zIndex: 1001,
              }}
            />
            <div style={{
              position: "fixed", bottom: 0, left: 0, right: 0,
              background: "#fff", borderRadius: "12px 12px 0 0",
              zIndex: 1002, maxHeight: "60vh",
            }}>
              <div style={{
                padding: "16px 20px", textAlign: "center",
                fontSize: 16, fontWeight: 600, color: "#333",
                borderBottom: "1px solid #f0f0f0",
              }}>选择区/街道</div>
              <div style={{ overflowY: "auto", maxHeight: "calc(60vh - 52px)", paddingBottom: 20 }}>
                {districtList.map((d) => (
                  <div key={d} onClick={() => handleSelectDistrict(d)}
                    style={{
                      padding: "14px 20px", fontSize: 14,
                      borderBottom: "1px solid #f9f9f9", cursor: "pointer",
                      background: d === formData.district ? "#fff5f0" : "transparent",
                      color: d === formData.district ? PRIMARY : "#333",
                      fontWeight: d === formData.district ? 600 : 400,
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
